import uFuzzy from "@leeoniya/ufuzzy"
import { Information } from "../simulation/types"
import synonyms from "./roots-shoots-synonyms.json"
import { TreeItemNode } from "./types"

type SynonymEntry = {
  id: number
  label: string
  keywords: string[]
}

export type SearchNode = {
  contents: { id: number; text: string }
  children: SearchNode[]
  synonyms: string[]
}

// create an object that creates a dict from the entries in synonyms using the id attribute
const synonymsDict = synonyms.reduce(
  (acc, item) => {
    acc[item.id] = item
    return acc
  },
  {} as { [key: number]: SynonymEntry },
)

export const createTreeNode = (item: Information): TreeItemNode => {
  const treeNode: TreeItemNode = {
    contents: item,
    isExpanded: false,
    isLeafNode: !item.categories || item.categories.length === 0,
    synonyms: synonymsDict[item.id]?.keywords ?? [],
    children: [],
    parent: null,
  }

  if (item.categories) {
    treeNode.children = item.categories.map((child) => {
      const childNode = createTreeNode(child)
      childNode.parent = treeNode
      return childNode
    })
  }

  return treeNode
}

type SearchEntries = {
  haystack: string[]
  haystackMap: { [key: string]: number }
}

const prepNodesForSearch = (
  nodes: TreeItemNode[] | SearchNode[],
  preppedNodes: SearchEntries = { haystack: [], haystackMap: {} },
): SearchEntries => {
  for (const node of nodes) {
    const idx = preppedNodes.haystack.length
    const haystack = [node.contents.text, ...node.synonyms]
    // generate haystackMap that links the index of the haystack to the node id
    preppedNodes.haystack.push(...haystack)
    const haystackMap: { [key: number]: number } = {}
    haystackMap[idx] = node.contents.id
    preppedNodes.haystackMap = { ...preppedNodes.haystackMap, ...haystackMap }
    prepNodesForSearch(node.children, preppedNodes)
  }
  return preppedNodes
}

export const searchTreeFuzzy = (
  nodes: TreeItemNode[] | SearchNode[],
  query: string,
): Set<number> => {
  if (!query || query.length < 3) return new Set<number>()
  const searcher = new uFuzzy({ intraMode: 1 })
  const preppedNodes = prepNodesForSearch(nodes)
  const cleanQuery = query.trim()
  const idxs = searcher.filter(preppedNodes.haystack, cleanQuery)
  if (!idxs) return new Set<number>()
  const ids = new Set<number>()
  idxs.forEach((idx) => {
    const id = preppedNodes.haystackMap[idx]
    ids.add(id)
  })
  return ids
}

export const searchTree = (nodes: TreeItemNode[], query: string) => {
  if (!query || query.length < 3) return new Set<number>()

  const results = new Set<number>()
  const search = (node: TreeItemNode) => {
    if (node.contents.text.toLowerCase().includes(query.toLowerCase())) {
      results.add(node.contents.id)
    }
    if (
      node.synonyms.some((synonym) =>
        synonym.toLowerCase().includes(query.toLowerCase()),
      )
    ) {
      results.add(node.contents.id)
    }
    for (const child of node.children) {
      search(child)
    }
  }
  for (const node of nodes) {
    search(node)
  }
  return results
}

export const hasDescendant = (
  node: TreeItemNode,
  descendant: number[],
  firstNode: boolean = true,
) => {
  if (descendant.includes(node.contents.id)) {
    if (firstNode) return false
    return true
  }

  for (const child of node.children) {
    if (hasDescendant(child, descendant, false)) return true
  }

  return false
}
